import * as stylex from '@stylexjs/stylex';
import { useState, useCallback, useId, useEffect, useRef } from 'react';
import { LoadingSpinner, Stack, Text } from '@figma/fpl-components';
import cssVars from '@figma/fpl-tokens/css-vars';
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { safeAxios } from '@/services/safeAxios';
import { API_ROUTES } from '@/api/apiRoutes';
import { log } from '@/logger/logger.ts';
import { I18N_KEYS } from '@/language/keys';
import { getFilteredAcceptedFileTypes, getMimeTypeFromExtension } from '@/utils/fileTypes';
import { useResolvedAssets, useWorkflowStore } from '@/state/workflow.state';
import { assetUrl, assetViewUrl, firstFrameSrc, isBlobstoreResolved } from '@/utils/assetUrls';
import { getVideoMimeType } from '@/utils/urls';
import { RunType } from '@/enums/run-type.enum';
import { styles } from './FileUploader.styles';
import { FileUploadArea, type UploadPreview } from './FileUploadArea';
import type { AxiosProgressEvent } from 'axios';
import type { MediaAsset, UploadAssetResponse } from '@/types/api/assets';
import type { useUploadMediaAsset } from '@/hooks/useUploadMediaAsset';
import type { ReactNode } from 'react';

const logger = log.getLogger('FileUploader');

type SingleFileUploaderProps = {
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  id: string;
  initialFile?: File;
  isLoading?: boolean;
  height?: number;
  onInitialFileUpload?: () => void;
  onUploadStart?: () => void;
  onUploadEnd?: () => void;
  onUpload: (file: Partial<MediaAsset> | null) => void;
  value?: Partial<MediaAsset>;
  acceptedFileType?: string;
  runType: RunType;
};

type ControlledFileUploaderProps = Pick<
  SingleFileUploaderProps,
  'id' | 'disabled' | 'isLoading' | 'error' | 'helperText'
> & {
  uploadState: Pick<ReturnType<typeof useUploadMediaAsset>, 'dropzoneState' | 'uploadProgress' | 'isInProgress'>;
  children?: ReactNode;
};

export type FileUploaderProps = SingleFileUploaderProps | ControlledFileUploaderProps;

export const FileUploader = (props: FileUploaderProps) => {
  return 'uploadState' in props ? <ControlledFileUploader {...props} /> : <SingleFileUploader {...props} />;
};

// If a caller provides an uploadState prop, we use the controlled uploader component
// this allows the caller to own the upload process and styling of the component
// E.g. MediaIterator allows multi-file uploads
const ControlledFileUploader = ({
  uploadState,
  disabled = false,
  isLoading = false,
  error = false,
  helperText = '',
  children,
}: ControlledFileUploaderProps) => {
  const errorId = useId();
  const { t } = useTranslation();
  const { getRootProps, getInputProps, open } = uploadState.dropzoneState;
  const hasContent = children != null;

  return (
    <div
      {...getRootProps(hasContent ? { onClick: (event) => event.stopPropagation() } : undefined)}
      {...stylex.props(styles.dropzone)}
    >
      {hasContent ? (
        <Stack.Row wrap align="start" gap={cssVars.spacer.x1} {...stylex.props(styles.fileList)}>
          {children}
          {uploadState.isInProgress && (
            <div {...stylex.props(styles.progressTile)}>
              <LoadingSpinner size="sm" loadingText={t(I18N_KEYS.COMMON_COMPONENTS.PROGRESS.UPLOAD_ARIA)} />
            </div>
          )}
        </Stack.Row>
      ) : (
        <FileUploadArea
          media={null}
          height={120}
          progress={uploadState.uploadProgress}
          disabled={disabled || isLoading || uploadState.isInProgress}
          error={error ? { kind: 'required', message: helperText } : undefined}
          errorId={errorId}
          onRequestUpload={open}
        />
      )}
      {hasContent && error && (
        <Text.span id={errorId} variant="body-medium" color="danger" role="alert">
          {helperText}
        </Text.span>
      )}
      <input {...getInputProps({ 'aria-invalid': error, 'aria-describedby': error ? errorId : undefined })} />
    </div>
  );
};

const SingleFileUploader = ({
  id,
  value,
  onUpload,
  disabled = false,
  error = false,
  helperText = '',
  initialFile,
  isLoading = false,
  height,
  onInitialFileUpload,
  onUploadStart,
  onUploadEnd,
  acceptedFileType,
  runType,
}: SingleFileUploaderProps) => {
  const uniqueId = useId(); // to avoid conflicts with multiple upload components
  const { t } = useTranslation();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploadPending, setIsUploadPending] = useState(false);
  const [previewImage, setPreviewImage] = useState(() =>
    assetViewUrl(value, useWorkflowStore.getState().resolvedAssets),
  );
  const [previewFile, setPreviewFile] = useState<File>();
  const [uploadedFileType, setUploadedFileType] = useState(value?.type);
  const [hasError, setHasError] = useState(false);
  const isUploadingRef = useRef(false);
  const resolvedAssets = useResolvedAssets();

  const inputId = `file-upload-input-${id}-${uniqueId}`;
  const errorId = hasError || error ? `${inputId}-error` : undefined;
  const fileIsSet = (previewImage || assetUrl(value, resolvedAssets)) && !hasError;
  const isPickerDisabled = disabled || isLoading || isUploadPending;

  // Create a temporary URL to preview the selected file.
  // Revoke it when the selected file changes or the component unmounts.
  useEffect(() => {
    if (!previewFile) return;
    const objectUrl = URL.createObjectURL(previewFile);
    setPreviewImage(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [previewFile]);

  useEffect(() => {
    if (value) {
      setPreviewImage(assetViewUrl(value, resolvedAssets));
      setUploadedFileType(value.type);
    }
  }, [value, value?.thumbnailUrl, value?.type, resolvedAssets]);

  const uploadSuccess = useCallback(
    (uploadData: MediaAsset) => {
      setUploadProgress(100);
      // Read the store, not `resolvedAssets`: mergeResolvedAssets ran moments ago in the caller.
      setPreviewImage(assetViewUrl(uploadData, useWorkflowStore.getState().resolvedAssets));
      setUploadedFileType(uploadData.type);
      onUpload(uploadData);
    },
    [onUpload],
  );

  const onUploadProgress = useCallback((progressEvent: AxiosProgressEvent) => {
    const total = progressEvent.total ?? progressEvent.loaded;
    const progress = Math.round((progressEvent.loaded / total) * 95);
    setUploadProgress(progress);
  }, []);

  const uploadFile = useCallback(
    async (file: File) => {
      /**
       * Clear the previous input so Run cannot use it during upload.
       * Preview the selected image or video locally until the upload completes.
       */
      onUpload(null);
      onUploadStart?.();
      const fileName = file.name.toLowerCase();

      setPreviewImage(undefined);
      setPreviewFile(undefined);
      if (file.type.startsWith('video')) {
        setUploadedFileType('video');
        setPreviewFile(file);
      } else if (file.type.startsWith('image')) {
        setUploadedFileType('image');
        setPreviewFile(file);
      } else if (file.type.startsWith('audio')) {
        setUploadedFileType('audio');
        setPreviewImage('/audio.png');
      } else if (fileName.endsWith('.glb') || fileName.endsWith('.obj') || fileName.endsWith('.fbx')) {
        setUploadedFileType('3D');
        // Preview image doesn't work for heic files
      } else if (fileName.endsWith('.heic')) {
        setUploadedFileType('image');
      }

      const formData = new FormData();
      formData.append('file', file);
      const mimeType = file.type || getMimeTypeFromExtension(file.name);
      if (mimeType) {
        formData.append('type', mimeType);
      }

      try {
        setIsUploadPending(true);
        setUploadProgress(0);
        const response = await safeAxios.post<UploadAssetResponse>(API_ROUTES.assets.uploadAsset(), formData, {
          params: { runType },
          onUploadProgress,
        });
        useWorkflowStore.getState().mergeResolvedAssets(response.data.resolvedAssets ?? {});

        uploadSuccess(response.data.asset);
      } catch (err) {
        logger.error('Error uploading file', err);
        setHasError(true);
      } finally {
        setIsUploadPending(false);
        setPreviewFile(undefined);
        setUploadProgress(0);
        onUploadEnd?.();
      }
    },
    [onUpload, onUploadStart, onUploadEnd, onUploadProgress, uploadSuccess, runType],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[], _rejectedFiles?: FileRejection[], event?: DropEvent) => {
      if (event && 'preventDefault' in event) {
        event.preventDefault();
      }
      setHasError(false);
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        void uploadFile(file);
      }
    },
    [uploadFile],
  );

  useEffect(() => {
    if (initialFile && onInitialFileUpload && !isUploadingRef.current) {
      isUploadingRef.current = true;
      uploadFile(initialFile)
        .then(() => {
          onInitialFileUpload();
          isUploadingRef.current = false;
        })
        .catch((error) => {
          logger.error('Error uploading initial file', error);
        });
    }
  }, [initialFile, onInitialFileUpload, uploadFile]);
  const acceptedFileTypes = getFilteredAcceptedFileTypes(acceptedFileType);
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    multiple: false,
    disabled: isPickerDisabled,
  });

  const openFileUploader = useCallback(() => {
    const inputElement = document.getElementById(inputId);
    inputElement?.click();
  }, [inputId]);

  const isBlobstore = isBlobstoreResolved(value, resolvedAssets);
  let mediaUrl = assetUrl(value, resolvedAssets) || previewImage;
  // Blobstore has no poster image, so the video shows its own first frame as the placeholder.
  if (mediaUrl && isBlobstore && uploadedFileType === 'video') {
    mediaUrl = firstFrameSrc(mediaUrl);
  }

  const media: UploadPreview | null = fileIsSet
    ? {
        url: mediaUrl,
        type: uploadedFileType,
        mimeType: uploadedFileType === '3D' ? value?.mime : getVideoMimeType(value?.url),
        poster: isBlobstore ? undefined : previewImage,
      }
    : null;

  return (
    <div {...getRootProps({ 'aria-describedby': errorId })} {...stylex.props(styles.dropzone)}>
      <FileUploadArea
        media={media}
        progress={uploadProgress}
        disabled={isPickerDisabled}
        height={height}
        errorId={errorId}
        error={
          hasError
            ? { kind: 'upload', message: t(I18N_KEYS.COMMON_COMPONENTS.FILE_UPLOADER.UPLOAD_ERROR_HELP) }
            : error
              ? { kind: 'required', message: helperText }
              : undefined
        }
        onRequestUpload={openFileUploader}
      />

      <input
        id={inputId}
        type="file"
        style={{ display: 'none' }}
        {...getInputProps({
          disabled: isPickerDisabled,
          'aria-invalid': hasError || error,
          'aria-describedby': errorId,
        })}
      />
    </div>
  );
};
